<template>
  <span
    aria-hidden="true"
    :class="[
      'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
      SIZE_CLASS[props.size ?? PJ_AVATAR_SIZE.MD],
      // A photo brings its own colour and needs no fill behind it; the two
      // fallbacks do, and they use the tonal container rather than the brand
      // fill, which belongs to whatever the screen's one action is.
      props.src ? 'bg-surface' : 'bg-container text-on-container'
    ]"
  >
    <img
      v-if="props.src"
      alt=""
      class="h-full w-full object-cover"
      :src="props.src"
    />

    <span
      v-else-if="props.initials"
      :class="['font-semibold leading-none', INITIALS_CLASS[props.size ?? PJ_AVATAR_SIZE.MD]]"
      >{{ props.initials }}</span
    >

    <!-- Inset at every size rather than filling the smallest one: a glyph flush
         to the edge of a circle reads as a cropped photo. -->
    <PjIcon
      v-else
      :name="PJ_ICON.YOU"
      size="inline"
    />
  </span>
</template>

<script lang="ts" setup>
import type { PjAvatarSize } from '@/domains/pjazza/components/atoms/pj-avatar/PjAvatar.types.ts'

import { PJ_AVATAR_SIZE } from '@/domains/pjazza/components/atoms/pj-avatar/PjAvatar.types.ts'
import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'

/**
 * A diner's face, their initials, or the generic mark — in that order.
 *
 * Three states rather than two, because the middle one is the common case for
 * a while: everybody gives a name at sign-up and almost nobody uploads a photo,
 * so initials are what most of these actually render. Falling straight from a
 * photo to the anonymous glyph would make a named diner look like a stranger.
 *
 * aria-hidden throughout. It is always beside the name it stands for, and an
 * avatar announced next to that name is the same thing said twice. The alt is
 * empty for the same reason.
 */
const props = defineProps<{
  src?: string | null
  initials?: string | null
  size?: PjAvatarSize
}>()

// nav is the header's account control, which sits beside an xs button — so it
// takes that button's height token rather than a number of its own. At 24px it
// read as a detail attached to the name; the two controls on that row are
// siblings and should present as one size. Tied to the token so a change to
// button height cannot leave the pair mismatched again.
const SIZE_CLASS: Record<'nav' | 'md' | 'lg', string> = {
  nav: 'h-btn-xs w-btn-xs',
  md: 'h-10 w-10',
  lg: 'h-[72px] w-[72px]'
}

const INITIALS_CLASS: Record<'nav' | 'md' | 'lg', string> = {
  nav: 'text-caption',
  md: 'text-item',
  lg: 'text-heading'
}
</script>
